import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "src/componentes";
import { AutenticacaoModule } from "src/Contexto/auth";
import { NFCeFiltrar, NFeFiltrar } from "src/services/Fiscal.Service";
import { NFCeConsultaInputDto, NFCeDto } from "src/pages/Privada/Fiscal/NFCe/NFCe.Dto";
import { NFeDto } from "src/pages/Privada/Fiscal/NFe/NFe.Dto";
import moment from "moment";
import { ApexOptions } from "apexcharts";
import { isPendenteCadastroUsuario } from "src/pages/Privada/CadastroInicial/CadastroInicial.Dto";

export interface DashboardData {
  totalFaturamento: number;
  totalNotas: number;
  totalNFe: number;
  totalNFCe: number;
  chartOptions: ApexOptions;
  chartSeries: any[];
}

export const InicioCode = () => {
  const { empresaId, userId, isLogged } = AutenticacaoModule();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);

  useEffect(() => {
    const Load = async () => {
      if (empresaId === 0 && isLogged) {
        showToast({
          message: "Efetue o cadastro inicial de sua empresa e usuário",
          title: "Atenção",
          variant: "Warning",
        });
        navigate("/cadastro-inicial/empresa");
        return;
      }

      if (isPendenteCadastroUsuario(userId) && isLogged) {
        navigate("/minhaconta");
        return;
      }

      if (empresaId > 0) {
        await carregarDadosDashboard();
      }
    };

    Load();
  }, [empresaId, userId, isLogged]);

  const carregarDadosDashboard = async () => {
    try {
      setLoading(true);
      const dataEmissaoInicial = moment().startOf('month').toDate();
      const dataEmissaoFinal = moment().endOf('month').toDate();

      const filtro: NFCeConsultaInputDto = {
        dataEmissaoInicial,
        dataEmissaoFinal,
        codigoStatusEnvio: "2", // Finalizada/Autorizada
      };

      const [resNFCe, resNFe] = await Promise.all([
        NFCeFiltrar(filtro),
        NFeFiltrar(filtro as any)
      ]);

      let nfces: NFCeDto[] = [];
      let nfes: NFeDto[] = [];

      if (resNFCe.data.success) {
        nfces = resNFCe.data.data;
      }
      if (resNFe.data.success) {
        nfes = resNFe.data.data;
      }

      processarDadosDashboard(nfces, nfes);
    } catch (error) {
      console.error(error);
      showToast({ message: "Erro inesperado ao carregar dashboard.", variant: "Warning" });
    } finally {
      setLoading(false);
    }
  };

  const processarDadosDashboard = (nfces: NFCeDto[], nfes: NFeDto[]) => {
    let totalFaturamentoReais = 0;
    
    const faturamentoPorDiaNFCe: Record<string, number> = {};
    const faturamentoPorDiaNFe: Record<string, number> = {};
    const diasSet = new Set<string>();

    nfces.forEach((nfce) => {
      const valor = nfce.total?.valorNotaFiscal || 0;
      totalFaturamentoReais += valor;

      if (nfce.emissaoDataHora) {
        const diaFormatado = moment(nfce.emissaoDataHora).format("DD/MM");
        faturamentoPorDiaNFCe[diaFormatado] = (faturamentoPorDiaNFCe[diaFormatado] || 0) + valor;
        diasSet.add(diaFormatado);
      }
    });

    nfes.forEach((nfe) => {
      const valor = nfe.total?.valorNotaFiscal || 0;
      totalFaturamentoReais += valor;

      if (nfe.emissaoDataHora) {
        const diaFormatado = moment(nfe.emissaoDataHora).format("DD/MM");
        faturamentoPorDiaNFe[diaFormatado] = (faturamentoPorDiaNFe[diaFormatado] || 0) + valor;
        diasSet.add(diaFormatado);
      }
    });

    const diasOrdenados = Array.from(diasSet).sort((a, b) => {
      return moment(a, "DD/MM").valueOf() - moment(b, "DD/MM").valueOf();
    });

    const seriesNFCe = diasOrdenados.map((dia) => faturamentoPorDiaNFCe[dia] || 0);
    const seriesNFe = diasOrdenados.map((dia) => faturamentoPorDiaNFe[dia] || 0);

    const chartOptions: ApexOptions = {
      chart: {
        type: 'area',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false },
        background: 'transparent'
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      xaxis: { categories: diasOrdenados },
      yaxis: {
        labels: {
          formatter: (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        }
      },
      tooltip: {
        y: {
          formatter: (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        }
      },
      colors: ['#0d6efd', '#17a44b'], // Azul para NFe, Verde para NFCe
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right'
      },
      theme: { mode: localStorage.getItem("theme") === "dark" ? "dark" : "light" }
    };

    setDashboardData({
      totalFaturamento: totalFaturamentoReais,
      totalNFCe: nfces.length,
      totalNFe: nfes.length,
      totalNotas: nfces.length + nfes.length,
      chartOptions,
      chartSeries: [
        { name: "Faturamento NFe", data: seriesNFe },
        { name: "Faturamento NFCe", data: seriesNFCe }
      ]
    });
  };

  return { loading, dashboardData };
};
